// "My kind of day", as a popover under its own calendar row.
//
// The row re-rates live while the sliders move, which is why the editor hangs
// BELOW the row rather than over it: the thing being edited stays in view.
//
// It edits the one recipe the rest of the site already knows (lib/recipe.ts) —
// the same model the map's lens menu edits, in the same order-not-weights form
// (lib/recipe-ranks.ts). Nothing here knows whether it is running in the app or
// on a guide: the host hands it the recipe and takes the edits back.

import { PRESETS, clampRecipe } from '../../lib/recipe.ts';
import { weightsFromRanks, ranksFromWeights } from '../../lib/recipe-ranks.ts';
import type { Recipe, WeightKey } from '../../lib/recipe.ts';

export interface MydayHost {
  recipe(): Recipe;
  /** `defer` marks a tick of a drag rather than the end of one. */
  setRecipe(r: Recipe, opts?: { defer?: boolean }): void;
}

const LABEL: Record<WeightKey, string> = { dry: 'Dry', calm: 'Light wind', sunny: 'Sunny sky', clear: 'Clear air', warm: 'Warm enough' };
const PRESET_ROWS: { key: keyof typeof PRESETS; label: string }[] = [
  { key: 'beach', label: 'Sunbathing' },
  { key: 'relax', label: 'Easy outdoors' },
  { key: 'hike', label: 'A cool walk' },
];
// What the editor ranks. A wind-gated recipe (made on the map) keeps its gate;
// its own ranking swaps "light wind" for "warm enough".
const visible = (r: Recipe): WeightKey[] => (r.gate === 'ws' ? ['dry', 'sunny', 'clear', 'warm'] : ['dry', 'calm', 'sunny', 'clear']);
const WEIGHT_WORD = ['most', 'more', 'some', 'a little'];

let open: { el: HTMLElement; host: MydayHost; onClose: () => void } | null = null;

export const mydayOpen = (): boolean => !!open;

export function closeMyday(): void {
  if (!open) return;
  const o = open;
  open = null;
  o.el.remove();
  o.onClose();
}

function tolOf(r: Recipe): number {
  const max = r.gate === 'ws' ? 10 : 15;
  const min = r.gate === 'ws' ? 0.5 : 1;
  return Math.min(max, Math.max(min, r.trap[1] - r.trap[0], r.trap[3] - r.trap[2]));
}

function render(): void {
  if (!open) return;
  const { el, host } = open;
  const r = host.recipe();
  const ws = r.gate === 'ws';
  const unit = ws ? ' m/s' : '°';
  const tol = tolOf(r);
  const order = ranksFromWeights(r.w, visible(r));
  const range = ws ? { min: 0, max: 25, step: 0.5, tmin: 0.5, tmax: 10 } : { min: -10, max: 40, step: 1, tmin: 1, tmax: 15 };
  el.innerHTML =
    `<div class="pl-md-head"><span>What makes a day good?</span><button type="button" class="pl-md-close" data-md-close aria-label="Close">✕</button></div>`
    + `<div class="pl-md-presets"><span>Start from</span>${PRESET_ROWS.map((p) => `<button type="button" class="pl-chip" data-md-preset="${p.key}">${p.label}</button>`).join('')}</div>`
    + `<div class="pl-md-line">A good day ${ws ? 'has wind of' : 'feels like'} <b>${r.trap[1]}–${r.trap[2]}${ws ? ' m/s' : ' °C'}</b></div>`
    + `<div class="pl-md-sliders">`
    + `<span>Ideal from</span><input type="range" data-md="b" min="${range.min}" max="${range.max}" step="${range.step}" value="${r.trap[1]}" aria-label="Ideal from"><output>${r.trap[1]}${unit}</output>`
    + `<span>Ideal to</span><input type="range" data-md="c" min="${range.min}" max="${range.max}" step="${range.step}" value="${r.trap[2]}" aria-label="Ideal to"><output>${r.trap[2]}${unit}</output>`
    + `<span>Tolerance</span><input type="range" data-md="tol" min="${range.tmin}" max="${range.tmax}" step="${range.step}" value="${tol}" aria-label="Tolerance"><output>±${tol}${unit}</output>`
    + `</div>`
    + `<div class="pl-md-order-head"><span>What matters, in order</span><span>↑ to move up</span></div>`
    + `<div>${order.map((k, i) => `<div class="pl-md-rank"><span class="pl-md-rank-n">${i + 1}</span><span class="pl-md-rank-label">${LABEL[k]}</span><span class="pl-md-rank-w">${WEIGHT_WORD[Math.min(i, WEIGHT_WORD.length - 1)]}</span><button type="button" class="pl-md-up" data-md-up="${k}"${i === 0 ? ' disabled' : ''} aria-label="Move ${LABEL[k]} up">↑</button></div>`).join('')}</div>`
    + `<div class="pl-md-foot"><span>The row above re-rates as you drag. Poor / Fair / Good / Ideal, like the others.</span><button type="button" class="pl-btn" data-md-close>Done</button></div>`;
}

function fromInputs(el: HTMLElement, host: MydayHost, moved: string): Recipe {
  const r = host.recipe();
  const val = (k: string) => Number(el.querySelector<HTMLInputElement>(`[data-md="${k}"]`)!.value);
  let b = val('b'), c = val('c');
  const tol = val('tol');
  // The two ends push each other rather than cross.
  if (moved === 'b' && b > c) c = b;
  if (moved === 'c' && c < b) b = c;
  return clampRecipe({ gate: r.gate, trap: [b - tol, b, c, c + tol], w: r.w });
}

/** Open the editor inside `anchor` (the calendar row's own slot). */
export function openMyday(anchor: HTMLElement, host: MydayHost, onClose: () => void): void {
  if (open && open.el.parentElement === anchor) return;
  closeMyday();
  const el = document.createElement('div');
  el.className = 'pl-myday';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-label', 'My kind of day');
  anchor.append(el);
  open = { el, host, onClose };
  render();

  el.addEventListener('click', (e) => {
    e.stopPropagation(); // a click inside never counts as a click outside
    const t = e.target as HTMLElement;
    if (t.closest('[data-md-close]')) { closeMyday(); return; }
    const preset = t.closest<HTMLElement>('[data-md-preset]');
    if (preset) {
      host.setRecipe(clampRecipe(PRESETS[preset.dataset.mdPreset as keyof typeof PRESETS]));
      render();
      return;
    }
    const up = t.closest<HTMLElement>('[data-md-up]');
    if (up) {
      const r = host.recipe();
      const order = ranksFromWeights(r.w, visible(r));
      const from = order.indexOf(up.dataset.mdUp as WeightKey);
      if (from > 0) {
        order.splice(from - 1, 0, ...order.splice(from, 1));
        host.setRecipe(clampRecipe({ ...r, w: weightsFromRanks(order, r.w) }));
        render();
      }
    }
  });
  // While dragging, only the figures beside the sliders are rewritten: replacing
  // the slider under the pointer would end the drag it is in the middle of.
  el.addEventListener('input', (e) => {
    const input = e.target as HTMLInputElement;
    if (!input.dataset || !input.dataset.md) return;
    const next = fromInputs(el, host, input.dataset.md);
    host.setRecipe(next, { defer: true });
    const r = host.recipe();
    const unit = r.gate === 'ws' ? ' m/s' : '°';
    const outs = el.querySelectorAll('output');
    const set = (k: string, v: number) => { const i = el.querySelector<HTMLInputElement>(`[data-md="${k}"]`); if (i && i !== input) i.value = String(v); };
    set('b', r.trap[1]);
    set('c', r.trap[2]);
    outs[0].textContent = `${r.trap[1]}${unit}`;
    outs[1].textContent = `${r.trap[2]}${unit}`;
    outs[2].textContent = `±${tolOf(r)}${unit}`;
    const line = el.querySelector('.pl-md-line b');
    if (line) line.textContent = `${r.trap[1]}–${r.trap[2]}${r.gate === 'ws' ? ' m/s' : ' °C'}`;
  });
  // Letting go is the decision; that is what is worth counting and persisting.
  el.addEventListener('change', (e) => {
    const input = e.target as HTMLInputElement;
    if (input.dataset && input.dataset.md) host.setRecipe(fromInputs(el, host, input.dataset.md));
  });
}
